import * as angular from "angular";
import {
	cloneValue,
	FormRuntime,
	JsonSchemaDefinition
} from "../runtime";
import type {
	FormChange,
	FormOptions,
	FormSession,
	FormValidationRequest,
	FormValidationResult
} from "./form-session";
import {
	clearHiddenTimeZones,
	hasPendingEdits
} from "./form-session";
import { validationResult } from "./validation-result";
import { TimeZoneContext } from "../TimeZoneContext";
import type { FormHandle } from "../FormHandle";
import template from "./form.html";
import node from "./node";
import validationMessages from "./validation-messages";
import { hasInvalidVisibleInput } from "./native-validation";
import "./form.less";

interface FormScope extends angular.IScope {
	$$postDigest(callback: () => void): void;
}

export interface FormController {
	schema: JsonSchemaDefinition;

	options?: FormOptions;

	formControl?: FormHandle;

	ngModel: angular.INgModelController;

	onChange?: (args: FormChange) => void;
}

// AngularJS owns the merged binding and required-controller properties.
// eslint-disable-next-line no-redeclare
export class FormController implements angular.IController {
	public static $inject = ["$scope", "$element"];

	public session?: FormSession;

	public configurationError?: string;

	public result: FormValidationResult;

	public nativeForm?: angular.IFormController;

	private removeValidationListener?: () => void;

	private removeNativeValidationListener?: () => void;

	private destroyed = false;

	private pendingReset?: { data: unknown };

	public constructor(
		private $scope: FormScope,
		private $element: angular.IAugmentedJQuery
	) {}

	public $onInit(): void {
		this.formControl = { reset: data => this.reset(data) };
		this.ngModel.$render = () => this.render();
		this.removeNativeValidationListener = this.$scope.$watchGroup([
			() => this.nativeForm?.$valid,
			() => this.session?.runtime.errors,
			() => this.session?.pendingEdits?.size ?? 0
		], () => {
			if(this.session) {
				this.publish();
			}
		});
		this.removeValidationListener = this.$scope.$on("schemaFormValidate", (_event, payload?: FormValidationRequest | string) => {
			const request = typeof payload === "object" ? payload : undefined;

			if(this.session) {
				this.session.showErrors = true;
				this.session.revision++;
				this.publish();
				request?.onValidated?.(cloneValue(this.session.runtime.data), this.result);
			} else {
				request?.onValidated?.(this.ngModel.$viewValue, {
					valid: false,
					messages: [this.configurationError ?? "This form cannot be validated."]
				});
			}
		});
	}

	public $onChanges(changes: angular.IOnChangesObject): void {
		if(this.session && changes.schema) {
			this.render(true);
			return;
		}

		if(this.session && changes.options) {
			this.session.readonly = this.isReadonly();
			this.session.revision++;
		}
	}

	public $onDestroy(): void {
		this.destroyed = true;
		this.pendingReset = undefined;
		this.formControl = undefined;
		this.removeValidationListener?.();
		this.removeNativeValidationListener?.();
	}

	private reset(data: unknown): void {
		if(this.destroyed) {
			return;
		}

		const request = { data: cloneValue(data) };
		this.pendingReset = request;

		// Parent changes must reach schema and ngModel before this reset wins.
		this.$scope.$$postDigest(() => {
			if(this.destroyed || this.pendingReset !== request) {
				return;
			}

			this.pendingReset = undefined;
			this.$scope.$apply(() => {
				this.ngModel.$setViewValue(request.data);
				this.render(true);
			});
		});
		this.$scope.$evalAsync();
	}

	private render(reset = false): void {
		// Equal model echoes do not replace the active form session or its drafts.
		if(!reset
			&& this.session?.runtime.schema === this.schema
			&& angular.equals(this.ngModel.$viewValue, this.session.runtime.data)) {
			return;
		}

		try {
			const runtime = new FormRuntime(this.schema, this.ngModel.$viewValue);
			this.configurationError = undefined;
			this.session = {
				runtime,
				timeZones: new TimeZoneContext(),
				referenceLabels: new Map(),
				revision: 0,
				readonly: this.isReadonly(),
				showErrors: false,
				update: (path, value) => {
					if(!this.session || this.session.readonly) {
						return;
					}

					runtime.update(path, value);
					this.session.revision++;
					this.publish();
				}
			};

			this.$scope.$evalAsync(() => {
				if(!this.destroyed) {
					this.publish(reset);
				}
			});
		}catch(error) {
			this.session = undefined;
			this.configurationError = "This form cannot be displayed because its schema is invalid.";
			this.ngModel.$setValidity("jsonSchema", false);
			this.onChange?.({
				value: this.ngModel.$viewValue,
				result: {
					valid: false,
					messages: [this.configurationError]
				}
			});
		}
	}

	private isReadonly(): boolean {
		return Boolean(
			this.schema.readOnly
			|| this.options?.readonly
			|| this.options?.readOnly
			|| this.$element[0].closest("fieldset[disabled]")
		);
	}

	private publish(reset = false): void {
		if(!this.session) {
			return;
		}

		if(clearHiddenTimeZones(this.session)) {
			this.session.revision++;
		}

		this.result = validationResult(this.session.runtime);

		if(hasPendingEdits(this.session)) {
			this.result.valid = false;
			this.result.messages.push("Save or cancel the open entry before continuing.");
		}

		if(this.nativeForm?.$valid === false && hasInvalidVisibleInput(this.$element[0])) {
			this.result.valid = false;

			if(!this.result.messages.length) {
				this.result.messages = ["Please correct the highlighted fields."];
			}
		}
		const data = cloneValue(this.session.runtime.data);

		if(!angular.equals(data, this.ngModel.$viewValue)) {
			this.ngModel.$setViewValue(data);
		}

		this.ngModel.$setValidity("jsonSchema", this.result.valid);

		if(reset) {
			this.nativeForm?.$setPristine();
			this.nativeForm?.$setUntouched();
			this.ngModel.$setPristine();
			this.ngModel.$setUntouched();
		}

		this.onChange?.({
			value: data,
			result: this.result,
			reset
		});
	}
}

export default angular.module("vesl.extensions.json-schema.renderer", [node, validationMessages])
	.component("jsonSchemaRenderer", {
		template,
		controller: FormController,
		bindings: {
			schema: "<",
			options: "<?",
			formControl: "=?",
			onChange: "&?"
		},
		require: { ngModel: "ngModel" }
	})
	.name;
